import { LitElement, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import {
  USER_ACCOUNT_SETTINGS_MODE,
  USER_ACCOUNT_SETTINGS_ORIENTATION,
  USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE,
  USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT,
  IProfileContentCustomizations,
  USER_ACCOUNT_SETTINGS_TAB_LIST,
  User,
} from "@/types";
import {
  dashboardClass,
  pageClasses,
  modalClasses,
  modalWidthClass,
  dashboardWidthClass,
  modalContainerClass,
  modalLayoutClass,
  BREAKPOINTS,
} from "@/constants";
import { cn, waitForAuthConfigured } from "@/utils";
import { closeIcon } from "@/assets/icons";
import "./profile";
import "./sidebar/sidebar";
import "./profile/sb-profile";
import { Account } from "./profile/components/account-info";
import { PersonalInfo } from "./profile/components/personal-info";
import { LocationInfo } from "./profile/components/location-info";
import { URLsInfo } from "./profile/components/urls-info";
import { userContext, UserContextValue } from "@/context";
import { provide } from "@lit/context";
import { getUser } from "@/service";
import { keyed } from "lit/directives/keyed.js";
import { Auth } from "@saasbase-io/loginflow-websdk";
import { closeAccountSettingsModal } from "@/events";

export class SbAccountSettings extends LitElement {
  // === Public Properties ===
  @property({ type: Boolean }) showAvatarMenu = false;
  @property({ type: String }) orientation: USER_ACCOUNT_SETTINGS_ORIENTATION =
    USER_ACCOUNT_SETTINGS_ORIENTATION.HORIZONTAL;
  @property({ type: String })
  mobileDisplay: USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE =
    USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE.REGULAR;
  @property({ type: String, reflect: true }) mode: USER_ACCOUNT_SETTINGS_MODE =
    USER_ACCOUNT_SETTINGS_MODE.PAGE;
  @property({ type: String })
  widgetsLayout: USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT =
    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW;
  @property({ type: Object }) content: IProfileContentCustomizations | null =
    null;

  // === Internal State ===
  @state() private isOpen = false; // Modal starts closed, page mode will set to true later
  @state() private currentBreakpoint: keyof typeof BREAKPOINTS = "md";
  @state() private activeTab: USER_ACCOUNT_SETTINGS_TAB_LIST =
    USER_ACCOUNT_SETTINGS_TAB_LIST.PROFILE;
  @state() private _userLoaded = false;
  @state() private _userLoading = false;

  @provide({ context: userContext })
  @state()
  private userContextValue: UserContextValue = {
    user: null,
    identifier: null,
  };

  private __initialized = false;

  // Default Profile Data
  private account: Account = {
    email: "",
    phone: "",
    avatar: "",
  };
  private personal: PersonalInfo = {
    username: "",
    first: "",
    middle: "",
    last: "",
    nickname: "",
    gender: "",
    bio: "",
  };
  private location: LocationInfo = {
    location: "",
    zoneInfo: "",
  };
  private urls: URLsInfo = { website: "" };

  // === Getters ===
  private get isModal() {
    return this.mode === USER_ACCOUNT_SETTINGS_MODE.MODAL;
  }

  private get isVerticalOrientation() {
    return this.orientation === USER_ACCOUNT_SETTINGS_ORIENTATION.VERTICAL;
  }

  private get isHorizontalOrientation() {
    return this.orientation === USER_ACCOUNT_SETTINGS_ORIENTATION.HORIZONTAL;
  }

  get isBurger() {
    return (
      this.sidebarDisplayType ===
      USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE.BURGER
    );
  }

  private get sidebarDisplayType(): USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE {
    if (this.isVerticalOrientation) {
      return USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE.REGULAR;
    }
    return this.currentBreakpoint === "md"
      ? USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE.REGULAR
      : USER_ACCOUNT_SETTINGS_SIDEBAR_DISPLAY_TYPE.BURGER;
  }

  private get layoutKey() {
    return `${this.orientation}|${this.mode}|${this.widgetsLayout}|${this.mobileDisplay}`;
  }

  private get containerClasses() {
    const isBurgerOrVertical = this.isBurger || this.isVerticalOrientation;
    return cn(
      dashboardClass,
      this.isModal ? modalClasses : pageClasses,
      this.isModal ? modalWidthClass : dashboardWidthClass,
      isBurgerOrVertical ? "!sb-flex-col" : "!sb-flex-row",
    );
  }

  private get contentClasses() {
    return cn(
      "sb-flex-grow",
      this.isModal
        ? `sb-account-settings-modal-content ${this.isHorizontalOrientation ? "sb-account-settings-modal-content-horizontal" : ""}`
        : "sb-account-settings-content-md-max-height-full",
      this.isVerticalOrientation ? "sb-account-settings-vertical-margin" : "",
    );
  }

  constructor() {
    super();
    // Bind once and attach listeners immediately (before connectedCallback)
    this._handleOpenProfile = this._handleOpenProfile.bind(this);
    this._handleCloseProfile = this._handleCloseProfile.bind(this);
    window.addEventListener("open-user-profile", this._handleOpenProfile);
    window.addEventListener("close-user-profile", this._handleCloseProfile);
  }

  // === Lifecycle ===
  connectedCallback() {
    super.connectedCallback();
    this._updateBreakpoint();
    window.addEventListener("resize", this._handleResize);
    document.addEventListener("remove-avatar", this._handleAvatarRemove);
    document.addEventListener("click", this._handleClickOutside);
    // FIX: Both open and close events are now listened on `document`
    document.addEventListener("close-user-profile", this._handleCloseProfile);
    document.addEventListener("open-user-profile", this._handleOpenProfile);
    document.addEventListener("change-avatar", this._handleAvatarChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("resize", this._handleResize);
    document.removeEventListener("remove-avatar", this._handleAvatarRemove);
    document.removeEventListener("click", this._handleClickOutside);
    document.removeEventListener(
      "close-user-profile",
      this._handleCloseProfile,
    );
    document.removeEventListener("open-user-profile", this._handleOpenProfile);
    document.removeEventListener("change-avatar", this._handleAvatarChange);
  }

  protected updated(changed: Map<string, unknown>) {
    // react to a change in the 'mode' property
    if (changed.has("mode")) {
      if (this.isModal) {
        this.isOpen = false; // modal should start closed
      } else {
        this.isOpen = true; // page mode is always visible
      }
    }

    if (changed.has("userContextValue") && this.userContextValue.user) {
      this.tryRemoveFallback();
    }
  }

  protected firstUpdated() {
    if (this.__initialized) return;
    this.__initialized = true;

    this.style.width = "100%";
    this.style.display = "flex";
    this.style.justifyContent = "center";
    this.style.flexDirection = "column";

    void this.loadUser();
  }

  // === Data Loading ===
  private fillProfileData(user: User) {
    this.account = {
      ...this.account,
      avatar: user.picture || "",
      email: user.email || "",
      phone: user.phone || "",
    };
    this.personal = {
      ...this.personal,
      bio: user.profile || "",
      first: user.given_name || "",
      last: user.family_name || "",
      username: user.username || "",
      gender: user.gender || "",
      middle: user.middle_name || "",
      nickname: user.nickname || "",
    };
    this.location = {
      ...this.location,
      location: user.address?.locality || "",
      zoneInfo: user.zoneinfo || "",
    };
    this.urls = { ...this.urls, website: user.website || "" };
  }

  private async loadUser() {
    await waitForAuthConfigured(Auth);

    this.userContextValue.identifier = Auth.getAuthenticatedUser()?.email!;
    // this.userContextValue.identifier = "a.jafarian@revotech.com.au";

    if (!this.userContextValue.identifier) {
      return;
    }

    this._userLoading = true;

    try {
      this.userContextValue.user = await getUser(
        this.userContextValue.identifier,
      );
      this.tryRemoveFallback();
      this.fillProfileData(this.userContextValue.user);
      this._userLoaded = true;
    } catch (err) {
      console.error("[sb-account-settings] Failed loading user:", err);
    } finally {
      this._userLoading = false;
    }
  }

  private tryRemoveFallback() {
    requestAnimationFrame(() => {
      const root = this as HTMLElement;
      const fallback = root.querySelector('[part="fallback"]');
      if (fallback) {
        fallback.remove();
      } else {
        console.warn("[sb-signup] fallback not found yet");
      }
    });
  }

  // === Event Handlers ===
  private _handleResize = () => {
    this._updateBreakpoint();
  };

  private _updateBreakpoint() {
    const width = window.innerWidth;
    this.currentBreakpoint =
      width < BREAKPOINTS.xs ? "xs" : width < BREAKPOINTS.sm ? "sm" : "md";
  }

  private _handleCloseProfile = () => {
    // Only affect modal mode; page mode should never be closed via events
    if (this.isModal) {
      this.isOpen = false;
    }
  };

  private _handleOpenProfile = () => {
    console.log("listening");
    if (this.isModal) {
      this.isOpen = true;
    }
  };

  private _toggleAvatarMenu = (e: Event) => {
    e.stopPropagation();
    this.showAvatarMenu = !this.showAvatarMenu;
  };

  private _handleAvatarChange = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        this.account.avatar = reader.result as string;
        this.requestUpdate();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  private _handleAvatarRemove = () => {
    this.account.avatar = "https://via.placeholder.com/80";
    this.showAvatarMenu = false;
  };

  private _handleModalOutsideClick = (e: MouseEvent) => {
    const modal = this.querySelector("[part='modal-content']");
    if (!modal) return;

    const path = e.composedPath();
    if (!path.includes(modal as EventTarget)) {
      this.dispatchEvent(
        new CustomEvent("close-user-profile", {
          bubbles: true,
          composed: true,
        }),
      );
    }
  };

  private _handleClickOutside = (e: MouseEvent) => {
    if (!this.showAvatarMenu) return;
    const target = e.target as HTMLElement;
    const avatarContainer = this.querySelector("sb-account-info");
    const avatarMenu = this.querySelector("rtg-card");
    if (avatarContainer?.contains(target) || avatarMenu?.contains(target))
      return;
    this.showAvatarMenu = false;
  };

  // === Render Helpers ===
  private renderWatermark() {
    if (!this.isVerticalOrientation) return nothing;
    return html`
      <div class="sb-account-settings-watermark-container">
        <sb-watermark custom-id="sb-watermark"></sb-watermark>
      </div>
    `;
  }

  private renderMainContent() {
    const orientationValue = this.isHorizontalOrientation
      ? "vertical"
      : "horizontal";

    return keyed(
      this.layoutKey,
      html`
        <div
          ?hidden=${this.isBurger}
          class=${cn(
            "sb-account-settings-header",
            this.isVerticalOrientation
              ? "sb-account-settings-header-vertical"
              : "",
            this.isBurger ? "!sb-hidden" : "",
          )}
        >
          <rtg-typography-h level="h3">Account Settings</rtg-typography-h>
          <rtg-button
            variant="ghost"
            size="icon-sm"
            part="modal-close-button"
            @click=${() => closeAccountSettingsModal()}
          >
            ${closeIcon()}
          </rtg-button>
        </div>
        <rtg-tabs
          orientation=${orientationValue}
          part="account-settings-tab"
          default-value=${USER_ACCOUNT_SETTINGS_TAB_LIST.PROFILE}
        >
          <div
            part=${this.isModal ? "page-content-modal" : "page-content"}
            class=${this.containerClasses}
          >
            <sb-profile-sidebar
              .orientation=${this.orientation}
              .mode=${this.mode}
              .mobileDisplay=${this.mobileDisplay}
              .displayType=${this.sidebarDisplayType}
              .activeTab=${this.activeTab}
              @tab-change=${(e: CustomEvent) => {
                this.activeTab = e.detail.tab;
              }}
            ></sb-profile-sidebar>
            <div class=${this.contentClasses}>
              <div class="sb-relative sb-account-settings-content-offset">
                ${this.renderTabContent()}
              </div>
            </div>
            ${this.renderWatermark()}
          </div>
        </rtg-tabs>
      `,
    );
  }

  private renderTabContent() {
    return html`
      <rtg-tabs-content value="Profile">
        <sb-profile
          .content=${this.content}
          .account=${this.account}
          .personal=${this.personal}
          .location=${this.location}
          .urls=${this.urls}
          .orientation=${this.orientation}
          .sidebarDisplayType=${this.sidebarDisplayType}
          .mode=${this.mode}
          .widgetsLayout=${this.widgetsLayout}
          .showAvatarMenu=${this.showAvatarMenu}
          .toggleAvatarMenu=${this._toggleAvatarMenu}
        ></sb-profile>
      </rtg-tabs-content>
      <rtg-tabs-content value=${USER_ACCOUNT_SETTINGS_TAB_LIST.SECURITY}>
        <sb-security-settings></sb-security-settings>
      </rtg-tabs-content>
    `;
  }

  // === Main Render ===
  render() {
    if (!this._userLoaded) {
      return html``;
    }
    if (!this.isOpen) return nothing;

    if (this.isModal) {
      return html`
        <div class="sb-account-settings-modal-overlay"></div>
        <div
          class=${modalContainerClass}
          @click=${this._handleModalOutsideClick}
        >
          <div part="modal-content" class=${modalLayoutClass}>
            ${this.renderMainContent()}
          </div>
        </div>
      `;
    }
    return this.renderMainContent();
  }

  createRenderRoot() {
    return this;
  }
}

customElements.define("sb-account-settings", SbAccountSettings);
